import { CircularProgress, MenuItem } from '@mui/material';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import React from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';

import { useGetCountryStatesQuery } from '@/api/v1';
import { Autocomplete } from '@/components/molecules/Autocomplete';
import { ControllerWithError, ControllerWithErrorParam } from '@/forms/ControllerWithError';
import { useStateCities } from '@/hooks/useStateCities';
import { useTranslator } from '@/hooks/useTranslator';
import { addressSchemaPartial } from '@/schemas/address';
import { CountryType } from '@/types/country';
import { NamedIdentified } from '@/types/namedIdentified';

type AddressForm = typeof addressSchemaPartial.infer;

interface Props<AddressSchema extends AddressForm> {
  readonly country: CountryType;
  readonly state: string | null;

  readonly extended?: boolean;
  readonly readOnly?: boolean;
  readonly control: Control<AddressSchema>;
  readonly setValue: UseFormSetValue<AddressSchema>;
}

export const AddressFields = <AddressSchema extends AddressForm>(
  props: Props<AddressSchema>,
): React.ReactElement => {
  const { extended = false, readOnly = false, country, state } = props;
  // TODO: fix this, this shouldn't be necessary
  const control = props.control as Control<AddressForm>;
  const setValue = props.setValue as UseFormSetValue<AddressForm>;
  const city = useWatch({
    control: control,
    name: 'address.city',
  });

  const { data: states = [], isLoading: statesLoading } = useGetCountryStatesQuery(country, {
    skip: !country,
  });
  const hasStates = states.length > 0;
  const stateCities = useStateCities(country, state);
  const cities = React.useMemo((): readonly string[] => {
    return stateCities
      .map((item: NamedIdentified): string => item.name)
      .filter((item: string): boolean => item.toLowerCase().includes(city.toLowerCase()));
  }, [city, stateCities]);

  const tr = useTranslator();

  React.useEffect((): void => {
    setValue('address.state', hasStates ? '' : null);
  }, [hasStates, setValue]);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
      <ControllerWithError
        name="address.street"
        label={tr('STREET_ADDRESS')}
        control={control}
        render={({
          field,
        }: ControllerWithErrorParam<AddressForm, 'address.street'>): React.ReactElement => (
          <TextField
            variant="outlined"
            fullWidth={true}
            {...field}
            slotProps={{ input: { readOnly } }}
          />
        )}
      />
      {extended && (
        <ControllerWithError
          name="address.complement"
          control={control}
          label={tr('ADDRESS_COMPLEMENT')}
          render={({
            field,
          }: ControllerWithErrorParam<AddressForm, 'address.complement'>): React.ReactElement => (
            <TextField
              variant="outlined"
              fullWidth={true}
              {...field}
              slotProps={{ input: { readOnly } }}
            />
          )}
        />
      )}
      <Box display="flex" alignItems="start" width="100%" gap={2}>
        {(hasStates || statesLoading) && (
          <ControllerWithError
            name="address.state"
            control={control}
            label={tr('STATE')}
            render={({
              field,
            }: ControllerWithErrorParam<AddressForm, 'address.state'>): React.ReactElement => (
              <TextField
                select={!statesLoading}
                variant="outlined"
                fullWidth={true}
                {...field}
                value={field.value ?? ''}
                disabled={statesLoading}
                slotProps={{
                  input: {
                    readOnly,
                    endAdornment: statesLoading ? (
                      <InputAdornment position="end">
                        <CircularProgress size={22} thickness={7} color="inherit" />
                      </InputAdornment>
                    ) : undefined,
                  },
                  inputLabel: { shrink: !!field.value },
                  select: { open: readOnly ? false : undefined },
                }}>
                {states.map((state: NamedIdentified): React.ReactElement => {
                  return (
                    <MenuItem key={state.id} value={state.id}>
                      {state.name}
                    </MenuItem>
                  );
                })}
                <MenuItem value="N/A">{tr('NOT_APPLICABLE')}</MenuItem>
              </TextField>
            )}
          />
        )}
        <ControllerWithError
          name="address.city"
          control={control}
          label={tr('CITY')}
          render={({
            field,
          }: ControllerWithErrorParam<AddressForm, 'address.city'>): React.ReactElement =>
            hasStates ? (
              <Autocomplete fullWidth={true} readOnly={readOnly} items={cities} {...field} />
            ) : (
              <TextField
                variant="outlined"
                fullWidth={true}
                {...field}
                slotProps={{ input: { readOnly } }}
              />
            )
          }
        />
        <ControllerWithError
          name="address.zipCode"
          control={control}
          label={tr('ZIP')}
          render={({
            field,
          }: ControllerWithErrorParam<AddressForm, 'address.zipCode'>): React.ReactElement => (
            <TextField fullWidth={true} {...field} slotProps={{ input: { readOnly } }} />
          )}
        />
      </Box>
    </Box>
  );
};
